'use client';
import { useEffect, useState } from 'react';

import { useTranslation } from '@/lib/i18n/i18n.client';
import { cn } from '@/lib/utils';

import type { ButtonProps } from '@/components/atoms/Button';
import { variantColors } from '@/components/atoms/Button/styles';
import { returnRefAndGenerateDataTracker } from '@/components/atoms/Button/utils';
import { Input, InputFieldProps } from '@/components/atoms/Input';
import {
  InputLabel,
  InputLabelProps,
} from '@/components/atoms/Input/InputLabel';
import { getSelectedDisplayValue } from '@/components/atoms/InputDropdown/utils';
import Popover, { PopoverProps } from '@/components/atoms/Popover';
import { SvgIcon } from '@/components/SvgIcon';

import { WithRef } from '@/cms/types/types';

import { TooltipProps } from '../Tooltip/Tooltip';

import iconChevronDownSrc from '~/svg/IconChevronDown.svg';
import iconSearchSrc from '~/svg/IconSearch.svg';

export const sizesOptions = ['m', 's'] as const;
const sizesButtonStyles: Record<(typeof sizesOptions)[number], string> = {
  s: 'h-8',
  m: 'h-10',
};

// mirror Button variant=input so we can asChild onto a host <button> (Button leaks props through Slot)
const inputTriggerStyles = cn(
  'w-fit font-serif font-medium inline-flex gap-2 items-center justify-center rounded-[4px] transition-colors px-4',
  'disabled:bg-gray-200 disabled:text-gray-500 disabled:pointer-events-none',
  variantColors.input.primary
);
const sizesDropdownItemStyles = {
  s: 'h-7',
  m: 'h-9',
};

export type DropdownOption = {
  label: string;
  value: string;
};

export type InputDropdownProps<T extends DropdownOption> = {
  options: T[];
  onOptionChange: (value: any) => void;
  value?: string;
  hasSearch?: boolean;
  isDisabled?: boolean;
  size?: (typeof sizesOptions)[number];
  classNames?: {
    root?: string;
    helpText?: string;
    search?: InputFieldProps['classNames'];
    inputField?: ButtonProps['classNames'];
    tooltip?: TooltipProps['classNames'];
    dropdown?: string;
    value?: string;
    listItem?: string;
  };
  renderOption?: ({ option }: { option: T }) => React.ReactNode;
  renderSelectedValue?: ({ option }: { option: T }) => React.ReactNode;
  popoverProps?: Partial<PopoverProps>;
  tooltip?: InputLabelProps['tooltip'];
} & Omit<InputFieldProps, 'classNames'> & {
    targetClassNames?: InputFieldProps['classNames'];
  };

export const InputDropdown = <T extends DropdownOption>({
  onOptionChange,
  options: propOptions,
  name,
  value,
  hasSearch,
  isDisabled,
  size = 'm',
  ref,
  classNames,
  renderOption,
  renderSelectedValue,
  ...props
}: WithRef<HTMLInputElement, InputDropdownProps<T>>) => {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [options, setOptions] = useState(propOptions);

  const { texts } = useTranslation();

  function handleSelect(value: T['value']) {
    onOptionChange(value);
    setIsOpen(false);
  }

  useEffect(() => {
    setOptions(propOptions);
  }, [propOptions]);

  const selectedDisplayValue = getSelectedDisplayValue({
    value,
    renderSelectedValue,
    options: propOptions,
    placeholder: props.placeholder,
    texts,
  });

  return (
    <div className={cn('relative w-full', classNames?.root)}>
      <div className='relative flex w-full flex-col'>
        {props.label && (
          <InputLabel
            label={props.label}
            required={props.isRequired}
            fieldName={name}
            classNames={{
              label: props.targetClassNames?.label?.root,
              tooltip: classNames?.tooltip,
            }}
            tooltip={props.tooltip}
          />
        )}
        <div className='mb-4'>
          <Popover
            {...props.popoverProps}
            // native <button> target; asChild avoids nested buttons from Radix Trigger
            targetProps={{ asChild: true, ...props.popoverProps?.targetProps }}
            classNames={{
              target: cn(
                'w-full group/input-dropdown',
                props.popoverProps?.classNames?.target
              ),
              childrenWrapper: cn(
                // no -mt-2: with asChild the trigger is the real anchor, negative margin overlaps it
                'border-gray-400 border py-2 px-0 rounded w-full min-w-[140px]',
                props.popoverProps?.classNames?.childrenWrapper
              ),
            }}
            position='bottom'
            showArrow={false}
            onChange={(value) => {
              if (!isDisabled) setIsOpen(value);
            }}
            isOpen={isDisabled ? false : isOpen}
            contentProps={{
              ...props.popoverProps?.contentProps,
              avoidCollisions: false,
              // panel is often wider than the trigger (e.g. phone country list)
              align: 'start',
              sideOffset: 4,
            }}
            target={
              <button
                type='button'
                disabled={isDisabled}
                className={cn(
                  inputTriggerStyles,
                  'w-full border justify-between text-left',
                  // asChild puts data-state on this same node
                  'data-[state=open]:border-brand-500 data-[state=open]:border-2',
                  sizesButtonStyles[size],
                  isDisabled && 'cursor-not-allowed bg-gray-100 text-gray-400',
                  classNames?.inputField?.root
                )}
              >
                {props.leftIcon ? (
                  <span
                    className={cn(
                      'flex flex-row items-center justify-center gap-1',
                      classNames?.inputField?.leftSection
                    )}
                  >
                    {props.leftIcon}
                  </span>
                ) : null}
                <span
                  className={cn(
                    'inline-block h-auto overflow-hidden text-ellipsis',
                    classNames?.inputField?.inner
                  )}
                >
                  <span
                    className={cn(
                      'text-body-m',
                      value
                        ? 'text-gray-950 dark:text-base-dark-white'
                        : 'text-gray-400',
                      classNames?.value
                    )}
                  >
                    {selectedDisplayValue}
                  </span>
                </span>
                <span
                  className={cn(
                    'flex flex-row items-center justify-center gap-1',
                    classNames?.inputField?.rightSection
                  )}
                >
                  <SvgIcon
                    src={iconChevronDownSrc}
                    className={cn(
                      'h-6 w-6 transition duration-500 ease-in-out',
                      isOpen && !isDisabled && 'rotate-180',
                      isDisabled && 'text-gray-400'
                    )}
                  />
                </span>
              </button>
            }
          >
            <div>
              {hasSearch && (
                <div className='px-4 py-1'>
                  <Input
                    size='s'
                    name='search'
                    hideBottomText={props.hideBottomText}
                    placeholder={texts.input_dropdown.search}
                    ref={() => setOptions(propOptions)}
                    onClick={(e) => e.stopPropagation()}
                    onChange={(e) => {
                      const value = e.target.value;

                      if (value === '') {
                        setOptions(propOptions);
                        return;
                      }
                      setOptions(
                        propOptions.filter((option) =>
                          option.label
                            .toLowerCase()
                            .includes(value.toLowerCase())
                        )
                      );
                    }}
                    leftIcon={<SvgIcon src={iconSearchSrc} />}
                    classNames={{
                      ...classNames?.search,
                      bottomText: {
                        ...classNames?.search?.bottomText,
                        root: cn(
                          'hidden',
                          classNames?.search?.bottomText?.root
                        ),
                      },
                    }}
                  />
                </div>
              )}
              <ul
                className={cn(
                  'max-h-80 overflow-y-auto text-gray-950 hover:no-underline',
                  classNames?.dropdown,
                  isOpen ? 'block' : 'hidden'
                )}
              >
                {options.length > 0 ? (
                  options.map((option, index) => (
                    <li
                      key={index}
                      className={cn(
                        'flex cursor-pointer items-center px-4 hover:bg-gray-50 dark:hover:bg-gray-100',
                        sizesDropdownItemStyles[size],
                        option.value === value && 'text-brand-500',
                        classNames?.listItem
                      )}
                      ref={(_ref) => {
                        returnRefAndGenerateDataTracker(_ref, ref);
                      }}
                      onClick={() => handleSelect(option.value)}
                      data-tracker-context={option.value}
                    >
                      {renderOption ? renderOption({ option }) : option.label}
                    </li>
                  ))
                ) : (
                  <li className={cn('p-1 px-4', classNames?.listItem)}>
                    {texts.input_dropdown.no_options}
                  </li>
                )}
              </ul>
            </div>
          </Popover>
        </div>
      </div>
    </div>
  );
};
